/**
 * Decides which editor is on screen. Keeping the switch here means pages only
 * ever say `setSheet({ kind, id })` and never import a modal.
 */
import { useApp } from '../store/AppContext.jsx';
import ClientSheet from './modals/ClientSheet.jsx';
import ClientView from './modals/ClientView.jsx';
import DealSheet from './modals/DealSheet.jsx';
import DealView from './modals/DealView.jsx';
import DealValueView from './modals/DealValueView.jsx';
import FollowUpView from './modals/FollowUpView.jsx';
import DemoShiftView from './modals/DemoShiftView.jsx';
import PipelineStatView from './modals/PipelineStatView.jsx';
import ClientStatView from './modals/ClientStatView.jsx';
import ReportListView from './modals/ReportListView.jsx';
import LinkClientSheet from './modals/LinkClientSheet.jsx';
import ContactSheet from './modals/ContactSheet.jsx';
import CompanySheet from './modals/CompanySheet.jsx';
import ActivitySheet from './modals/ActivitySheet.jsx';
import DraftModal from './modals/DraftModal.jsx';
import CampaignSheet from './modals/CampaignSheet.jsx';
import CampaignView from './modals/CampaignView.jsx';
import MarketingDetail from './modals/MarketingDetail.jsx';
import AudienceSheet from './modals/AudienceSheet.jsx';
import MktContactSheet from './modals/MktContactSheet.jsx';
import TemplateSheet from './modals/TemplateSheet.jsx';
import JourneySheet from './modals/JourneySheet.jsx';
import PageJourneySheet from './modals/PageJourneySheet.jsx';
import AIInsightSheet from './modals/AIInsightSheet.jsx';
import MoveToDealSheet from './modals/MoveToDealSheet.jsx';
import MoveToClientSheet from './modals/MoveToClientSheet.jsx';
import NotInterestedSheet from './modals/NotInterestedSheet.jsx';
import MoveStage from './modals/MoveStage.jsx';
import ImportLeads from './modals/ImportLeads.jsx';
import SendConsole from './modals/SendConsole.jsx';
import JourneyQueue from './modals/JourneyQueue.jsx';
import TrackCampaignSheet from './modals/TrackCampaignSheet.jsx';
import TrackCampaignView from './modals/TrackCampaignView.jsx';
import TrackLinkSheet from './modals/TrackLinkSheet.jsx';
import TrackLinkView from './modals/TrackLinkView.jsx';
import TrackPersonalise from './modals/TrackPersonalise.jsx';

export default function SheetHost() {
  const { sheet, setSheet, draft } = useApp();
  const close = () => setSheet(null);

  return (
    <>
      {sheet?.kind === 'client' ? <ClientSheet key={sheet.id || 'new'} id={sheet.id} onClose={close} /> : null}
      {sheet?.kind === 'clientView'
        ? <ClientView key={`v${sheet.id}`} id={sheet.id} onClose={close} /> : null}
      {sheet?.kind === 'deal'
        ? <DealSheet key={sheet.id || 'new'} id={sheet.id} manual={!!sheet.manual} onClose={close} />
        : null}
      {sheet?.kind === 'dealView'
        ? <DealView key={`v${sheet.id}`} id={sheet.id} onClose={close} /> : null}
      {sheet?.kind === 'dealValue'
        ? <DealValueView key={`dv${sheet.scope || 'all'}`} scope={sheet.scope} onClose={close} /> : null}
      {sheet?.kind === 'followUpView'
        ? <FollowUpView key={sheet.id} id={sheet.id} onClose={close} /> : null}
      {sheet?.kind === 'demoShift'
        ? <DemoShiftView key={sheet.id} id={sheet.id} onClose={close} /> : null}
      {/* One visit, event by event. A busy session is two hundred rows and
          belongs in a window, not under the row that opened it. */}
      {sheet?.kind === 'importLeads'
        ? <ImportLeads onClose={close} /> : null}
      {sheet?.kind === 'importContacts'
        ? <ImportLeads into="contacts" onClose={close} /> : null}
      {sheet?.kind === 'moveToDeal'
        ? <MoveToDealSheet id={sheet.id} onClose={close} /> : null}
      {/* `from` is which board pressed it. A lead can go straight onto the
          ledger without ever being a deal, and the client record says which
          of the two happened. */}
      {sheet?.kind === 'moveToClient'
        ? <MoveToClientSheet key={`mc${sheet.id}`} id={sheet.id} from={sheet.from} onClose={close} /> : null}
      {/* Two ways off the board, one sheet: they said no, or nobody could
          reach them. See MODES in NotInterestedSheet. */}
      {sheet?.kind === 'notInterested'
        ? <NotInterestedSheet key={`ni${sheet.id}`} id={sheet.id} onClose={close} /> : null}
      {sheet?.kind === 'noResponse'
        ? <NotInterestedSheet key={`nr${sheet.id}`} id={sheet.id} mode="quiet" onClose={close} /> : null}
      {/* Move a lead to another stage with the required "what happened" comment. */}
      {sheet?.kind === 'moveStage'
        ? <MoveStage key={`ms${sheet.id}`} id={sheet.id} onClose={close} /> : null}
      {/* The AI read of whatever is on screen. One panel for all seven
          modules, because they all answer in the same shape. */}
      {sheet?.kind === 'aiInsight'
        ? (
          <AIInsightSheet
            key={`ai${sheet.module}${sheet.id || ''}`}
            module={sheet.module}
            id={sheet.id}
            subject={sheet.subject}
            onClose={close}
          />
        ) : null}
      {/* `dealId` and `index` are how the window goes and reads the trail:
          the list response carries the counts and leaves the events behind,
          so a window opened without them has a badge and nothing under it. */}
      {sheet?.kind === 'pageJourney'
        ? (
          <PageJourneySheet
            visit={sheet.visit}
            dealId={sheet.dealId}
            index={sheet.index}
            who={sheet.who}
            at={sheet.at}
            onClose={close}
          />
        ) : null}
      {sheet?.kind === 'pipeStat'
        ? <PipelineStatView key={`ps${sheet.metric}`} metric={sheet.metric} onClose={close} /> : null}
      {/* The window travels with the metric — see ClientStatView. Keyed on
          both, so switching the strip and reopening redraws rather than
          reusing the list from the last window. */}
      {sheet?.kind === 'clientStat'
        ? (
          <ClientStatView
            key={`cs${sheet.metric}:${sheet.days ?? ''}`}
            metric={sheet.metric}
            days={sheet.days}
            onClose={close}
          />
        ) : null}
      {sheet?.kind === 'reportList'
        ? (
          <ReportListView
            key={`rl${sheet.title}`}
            title={sheet.title}
            note={sheet.note}
            tone={sheet.tone}
            icon={sheet.icon}
            rows={sheet.rows}
            onClose={close}
          />
        ) : null}
      {sheet?.kind === 'linkClient'
        ? <LinkClientSheet key={`lc${sheet.id}`} id={sheet.id} onClose={close} /> : null}
      {sheet?.kind === 'contact' ? <ContactSheet key={sheet.id || 'new'} id={sheet.id} onClose={close} /> : null}
      {sheet?.kind === 'company' ? <CompanySheet key={sheet.id || 'new'} id={sheet.id} onClose={close} /> : null}
      {sheet?.kind === 'activity'
        ? <ActivitySheet key={sheet.id || 'new'} id={sheet.id} dealId={sheet.dealId} onClose={close} />
        : null}

      {sheet?.kind === 'campaign'
        ? <CampaignSheet key={sheet.id || 'new'} id={sheet.id} preset={sheet.preset} onClose={close} /> : null}
      {sheet?.kind === 'campaignView'
        ? <CampaignView key={`v${sheet.id}`} id={sheet.id} onClose={close} /> : null}
      {sheet?.kind === 'marketingDetail'
        ? (
          <MarketingDetail
            key={`${sheet.metric}:${sheet.channel || 'all'}`}
            metric={sheet.metric}
            channel={sheet.channel}
            onClose={close}
          />
        ) : null}
      {sheet?.kind === 'trackCampaign'
        ? <TrackCampaignSheet key={sheet.id || 'new'} id={sheet.id} onClose={close} /> : null}
      {sheet?.kind === 'trackCampaignView'
        ? <TrackCampaignView key={`v${sheet.id}`} id={sheet.id} onClose={close} /> : null}
      {sheet?.kind === 'trackLink'
        ? <TrackLinkSheet key={sheet.id} id={sheet.id} onClose={close} /> : null}
      {sheet?.kind === 'trackLinkView'
        ? <TrackLinkView key={`v${sheet.id}`} id={sheet.id} onClose={close} /> : null}
      {sheet?.kind === 'trackPersonalise'
        ? <TrackPersonalise key={`p${sheet.id}`} id={sheet.id} onClose={close} /> : null}

      {sheet?.kind === 'audience'
        ? <AudienceSheet key={sheet.id || 'new'} id={sheet.id} onClose={close} /> : null}
      {sheet?.kind === 'mktContact'
        ? <MktContactSheet key={sheet.id || 'new'} id={sheet.id} onClose={close} /> : null}
      {sheet?.kind === 'template'
        ? <TemplateSheet key={sheet.id || 'new'} id={sheet.id} preset={sheet.preset} onClose={close} /> : null}
      {sheet?.kind === 'journey'
        ? <JourneySheet key={sheet.id || 'new'} id={sheet.id} onClose={close} /> : null}
      {sheet?.kind === 'send' ? <SendConsole key={sheet.id} id={sheet.id} onClose={close} /> : null}
      {sheet?.kind === 'queue' ? <JourneyQueue onClose={close} /> : null}

      {draft ? <DraftModal /> : null}
    </>
  );
}
